import { useCallback, useEffect, useState } from 'react'
import { supabase } from '../supabase'
import { useAuth } from '../auth'

/**
 * Per-user, per-game, per-day notes (the app version of the NOTES box
 * printed in the Portable Party Games book). One row per
 * (user, game, local day) — saving twice the same day updates that
 * day's note; a new day pins a fresh note. Backed by `user_game_notes`,
 * RLS-scoped to the current user.
 */

// `user_game_notes` isn't in the generated DB types, so use an untyped
// client (same pattern as user_mutes and other post-generation tables).
const db = supabase as any

export interface GameNote {
  note_date: string   // 'YYYY-MM-DD' — the writer's LOCAL day
  body: string
  updated_at: string
}

/** Today as 'YYYY-MM-DD' in the device's local timezone — "the day I
 *  wrote it" from the player's point of view, deliberately not UTC. */
export function localToday(): string {
  const d = new Date()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${m}-${day}`
}

export async function fetchGameNotes(gameKey: string): Promise<GameNote[]> {
  const { data, error } = await db
    .from('user_game_notes')
    .select('note_date, body, updated_at')
    .eq('game_key', gameKey)
    .order('note_date', { ascending: false })
  if (error) throw error
  return data ?? []
}

/** Insert or replace the current user's note for today. */
export async function saveTodayNote(gameKey: string, body: string): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser()
  if (!user) throw new Error('Not signed in')
  const { error } = await db
    .from('user_game_notes')
    .upsert(
      {
        profile_id: user.id,
        game_key: gameKey,
        note_date: localToday(),
        body,
        updated_at: new Date().toISOString(),
      },
      { onConflict: 'profile_id,game_key,note_date' },
    )
  if (error) throw error
}

export async function deleteGameNote(gameKey: string, noteDate: string): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser()
  if (!user) throw new Error('Not signed in')
  const { error } = await db
    .from('user_game_notes')
    .delete()
    .eq('profile_id', user.id)
    .eq('game_key', gameKey)
    .eq('note_date', noteDate)
  if (error) throw error
}

/**
 * Loads the current user's notes for one game. Guests (no session) get
 * an empty list and never hit the network — the screen shows a sign-in
 * nudge instead.
 */
export function useGameNotes(gameKey: string): {
  notes: GameNote[]
  loading: boolean
  refresh: () => void
} {
  const { session } = useAuth()
  const [notes, setNotes] = useState<GameNote[]>([])
  const [loading, setLoading] = useState(true)

  const refresh = useCallback(() => {
    if (!session) { setNotes([]); setLoading(false); return }
    setLoading(true)
    fetchGameNotes(gameKey)
      .then(setNotes)
      .catch(() => { /* non-fatal: keep whatever we had */ })
      .finally(() => setLoading(false))
  }, [session, gameKey])

  useEffect(() => { refresh() }, [refresh])

  return { notes, loading, refresh }
}
